import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  BindingCatalog,
  BindingMap,
  BindingValidationReport,
  RegionTree,
  TemplateAnchorRequest,
} from '@/types';

export interface UseDocxTemplateMappingResult {
  regions: RegionTree | null;
  catalog: BindingCatalog | null;
  isLoading: boolean;
  /** Non-null when the config has no template uploaded yet (409). */
  error: Error | null;
  isSaving: boolean;
  isValidating: boolean;
  validation: BindingValidationReport | null;
  /** Upload/replace the styled template; refreshes the region tree. */
  uploadTemplate: (file: File) => Promise<void>;
  /** Inject anchors for designated regions; returns the bookmark names in
   * request order **and** the refreshed region tree that now contains them.
   * The fresh tree is returned (not just pushed to state) so a caller can
   * re-nest in the same tick — `regions` state won't update until re-render,
   * and geometry re-nesting must see the just-injected bookmarks. */
  injectAnchors: (
    anchors: TemplateAnchorRequest[]
  ) => Promise<{ bookmarks: string[]; regions: RegionTree }>;
  /** Persist the binding map onto the config. */
  saveBindingMap: (map: BindingMap) => Promise<void>;
  /** Run the six bind-time rules against a draft map. */
  validate: (map: BindingMap) => Promise<BindingValidationReport>;
  refresh: () => void;
}

export function useDocxTemplateMapping(configId: string | null): UseDocxTemplateMappingResult {
  const [regions, setRegions] = useState<RegionTree | null>(null);
  const [catalog, setCatalog] = useState<BindingCatalog | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validation, setValidation] = useState<BindingValidationReport | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!configId) {
      setRegions(null);
      setCatalog(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    Promise.all([
      // A 404 here means "no template uploaded yet" — surfaced as error so
      // the page shows the upload prompt; the catalog loads regardless.
      vnextApiClient.getTemplateRegions(configId).catch((err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      }),
      vnextApiClient.getBindingCatalog(configId),
    ])
      .then(([tree, cat]) => {
        setRegions(tree);
        setCatalog(cat);
      })
      .catch((err) => {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        setError(e);
      })
      .finally(() => setIsLoading(false));
  }, [configId, reloadKey]);

  const uploadTemplate = useCallback(
    async (file: File) => {
      if (!configId) {
        return;
      }
      const response = await Sentry.startSpan(
        { op: 'http.client', name: 'uploadTemplateDocx' },
        () => vnextApiClient.uploadTemplateDocx(configId, file)
      );
      setRegions(response.regions);
      setError(null);
    },
    [configId]
  );

  const injectAnchors = useCallback(
    async (anchors: TemplateAnchorRequest[]) => {
      if (!configId) {
        throw new Error('No config id');
      }
      const response = await vnextApiClient.injectTemplateAnchors(configId, anchors);
      setRegions(response.regions);
      return { bookmarks: response.bookmarks, regions: response.regions };
    },
    [configId]
  );

  const saveBindingMap = useCallback(
    async (map: BindingMap) => {
      if (!configId) {
        return;
      }
      setIsSaving(true);
      try {
        await vnextApiClient.updateReportConfig(configId, { binding_map: map });
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [configId]
  );

  const validate = useCallback(
    async (map: BindingMap) => {
      if (!configId) {
        throw new Error('No config id');
      }
      setIsValidating(true);
      try {
        const report = await vnextApiClient.validateBindings(configId, map);
        setValidation(report);
        return report;
      } finally {
        setIsValidating(false);
      }
    },
    [configId]
  );

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  return {
    regions,
    catalog,
    isLoading,
    error,
    isSaving,
    isValidating,
    validation,
    uploadTemplate,
    injectAnchors,
    saveBindingMap,
    validate,
    refresh,
  };
}
